import React from 'react';
import { useRouteMatch } from 'react-router-dom';
import { Field, Formik } from 'formik';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle } from '@material-ui/core';
import * as Yup from 'yup';

import {
  GQLAccessorySize,
  GQLMaterialType,
  GQLParty,
  GQLPieceConfigurationInput,
  GQLPieceModel,
  GQLPieceModelInput,
  GQLPieceSize,
  GQLPieceWeightInput,
  GQLPlatingType,
  GQLStonesPlacementType
} from '@jewelutions/core-crm-server-api-client-domain';

import { JwlField, JwlSelect } from '../../../../components';
import { SelectOption } from '../../../../components/JwlSelect/JwlSelect';
import { removeTypename, stringListToDisplayValueList } from '../../../../core/utils';
import { GQLOperationBuilder } from '../../../../operations/GQLOperationBuilder';
import { ErrorMessage } from '../../../../types-hack';

interface IProps {
  pieceId: string;
  model: GQLPieceModel;
  party: GQLParty;
  onClose: (success: boolean) => void;
}

const availableStones: SelectOption[] = stringListToDisplayValueList(Object.values(GQLStonesPlacementType));
const availableAccessorySizes: SelectOption[] = stringListToDisplayValueList(Object.keys(GQLAccessorySize));

const CreateOrUpdatePieceModelDialog: React.FC<IProps> = ({ pieceId, model, onClose }) => {
  const rhodiumWeight = model.weights.find(w => w.configuration.platingType === GQLPlatingType.Rhodium);
  const roseGoldWeight = model.weights.find(w => w.configuration.platingType === GQLPlatingType.RoseGold);
  const yellowGoldWeight = model.weights.find(w => w.configuration.platingType === GQLPlatingType.YellowGold);

  const [updatePieceModel, { error, loading }] = GQLOperationBuilder.piece.updatePieceModel();

  const onSubmit = (props: any) => {
    const configurations: GQLPieceConfigurationInput[] = [];
    const weights: GQLPieceWeightInput[] = [];

    if (props.weights.rhodium > 0) {
      const c: GQLPieceConfigurationInput = {
        sizeType: model.sizeType,
        stonesPlacementType: model.stonesPlacementType,
        materialType: GQLMaterialType.Silver,
        platingType: GQLPlatingType.Rhodium
      };

      weights.push({
        configuration: c,
        weightInGrams: Number.parseFloat(props.weights.rhodium)
      });
      configurations.push(c);
    }

    if (props.weights.roseGold > 0) {
      const c: GQLPieceConfigurationInput = {
        sizeType: model.sizeType,
        stonesPlacementType: model.stonesPlacementType,
        materialType: GQLMaterialType.Silver,
        platingType: GQLPlatingType.RoseGold
      };

      weights.push({
        configuration: c,
        weightInGrams: Number.parseFloat(props.weights.roseGold)
      });
      configurations.push(c);
    }

    if (props.weights.yellowGold > 0) {
      const c: GQLPieceConfigurationInput = {
        sizeType: model.sizeType,
        stonesPlacementType: model.stonesPlacementType,
        materialType: GQLMaterialType.Silver,
        platingType: GQLPlatingType.YellowGold
      };

      weights.push({
        configuration: c,
        weightInGrams: Number.parseFloat(props.weights.yellowGold)
      });
      configurations.push(c);
    }

    const input: GQLPieceModelInput = {
      availableConfigurations: configurations,
      stonesPlacementType: model.stonesPlacementType,
      dimensions: {
        width: Number.parseFloat(props.dimensions.width),
        height: Number.parseFloat(props.dimensions.height),
        depth: Number.parseFloat(props.dimensions.depth)
      },
      sizeType: props.sizeType,
      weights: weights
    };

    const mutationVariables = { pieceId: pieceId, pieceModelId: model.id, input: input };

    updatePieceModel({ variables: mutationVariables }).then(_result => {
      onClose(true);
    });
  };

  const validationSchema = Yup.object({});

  const initialValues = {
    sizeType: model.sizeType,
    stonesPlacementType: model.stonesPlacementType,
    dimensions: {
      width: model.dimensions.width,
      height: model.dimensions.height,
      depth: model.dimensions.depth
    },
    weights: {
      rhodium: rhodiumWeight ? rhodiumWeight.weightInGrams : 0,
      roseGold: roseGoldWeight ? roseGoldWeight.weightInGrams : 0,
      yellowGold: yellowGoldWeight ? yellowGoldWeight.weightInGrams : 0
    }
  };

  return (
    <Dialog open aria-labelledby="model-dialog">
      <DialogTitle>{`Update piece model`}</DialogTitle>
      <DialogContent>
        <div>
          <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={onSubmit}>
            {({ values, handleSubmit }) => (
              <form onSubmit={handleSubmit} className="flex flex-col">
                <Field
                  name="sizeType"
                  component={JwlSelect}
                  label="Size Type"
                  options={availableAccessorySizes}
                />
                <ErrorMessage component="span" name="sizeType" className="fs-12 text-error" />

                <Field
                  name="stonesPlacementType"
                  component={JwlSelect}
                  label="Stone Placement Type"
                  options={availableStones}
                />

                <ErrorMessage component="span" name="stonesPlacementType" className="fs-12 text-error" />

                <div style={{ height: '200px' }}>
                  <span>Dimensions</span>
                  <Field name="dimensions.height" component={JwlField} label="Height" />
                  <ErrorMessage component="span" name="dimensions.height" className="fs-12 text-error" />

                  <Field name="dimensions.width" component={JwlField} label="Width" />
                  <ErrorMessage component="span" name="dimensions.width" className="fs-12 text-error" />

                  <Field name="dimensions.depth" component={JwlField} label="Depth" />
                  <ErrorMessage component="span" name="dimensions.depth" className="fs-12 text-error" />
                </div>

                <div style={{ height: '200px' }}>
                  <span>Availability (weight === 0 means not available)</span>
                  <Field name={`weights.rhodium`} component={JwlField} label="Rhodium" />
                  <ErrorMessage component="span" name={`weights.rhodium`} className="fs-12 text-error" />

                  <Field name={`weights.roseGold`} component={JwlField} label="Rose Gold" />
                  <ErrorMessage component="span" name={`weights.roseGold`} className="fs-12 text-error" />

                  <Field name={`weights.yellowGold`} component={JwlField} label="Yellow Gold" />
                  <ErrorMessage component="span" name={`weights.yellowGold`} className="fs-12 text-error" />
                </div>

                <DialogActions>
                  <Button color="primary" onClick={() => onClose(false)}>
                    Close
                  </Button>
                  <Button type="submit" color="primary" variant="contained">
                    Save
                  </Button>
                </DialogActions>
              </form>
            )}
          </Formik>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CreateOrUpdatePieceModelDialog;
